import { PortableText } from '@portabletext/react'
import Image from 'next/image'
import { useState } from 'react'

import Button from '~/components/Button'
import Card from '~/components/Card'
import CTA from '~/components/CTA'
import Eyebrow from '~/components/Eyebrow'
import { Column, Grid } from '~/components/Grid'
import SwitchToggle from '~/components/SwitchToggle'
import { SecondaryPage } from '~/lib/sanity.queries'

import { urlForImage } from '../../lib/sanity.image'

export default function Overview({
  content,
  title,
}: {
  content: SecondaryPage['overviewCards']
  title: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const toggleOpen = () => setIsOpen(!isOpen)

  return (
    <section>
      <Grid className="text-center mb-20">
        <Column className="col-span-12 relative mb-6 md:mb-12">
          <div className="flex items-center justify-center w-full">
            <div className="relative">
              <div
                className={`mono-small absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-full md:whitespace-nowrap pr-4 transition duration-300 ${!isOpen ? 'text-white' : 'text-grey'}`}
              >
                {title}
              </div>
              <SwitchToggle isOpen={isOpen} onClick={toggleOpen} />
              <div
                className={`mono-small absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-full text-left md:whitespace-nowrap pl-4 transition duration-300 ${isOpen ? 'text-white' : 'text-grey'}`}
              >
                We got you covered
              </div>
            </div>
          </div>
        </Column>
        <Column className="col-span-12 relative">
          <Card className="h-full w-full">
            {isOpen && <CardContent key="cardA" content={content[1]} />}
            {!isOpen && <CardContent key="cardB" content={content[0]} />}
          </Card>
        </Column>
      </Grid>
    </section>
  )
}

const CardContent = ({
  content,
}: {
  content: SecondaryPage['overviewCards'][0]
}) => {
  return (
    <div className="grid grid-cols-12 w-full h-full animate-fade-in-up">
      <div
        className="col-span-12 md:col-span-7 lg:col-span-6 flex items-start justify-center h-full w-full flex-col
      text-left relative flex-grow-0 origin-top transition-none"
      >
        <Eyebrow>{content.kicker}</Eyebrow>
        <h2 className="h5 text-gradient my-8 max-w-lg">
          <PortableText value={content.title} />
        </h2>
        <div className="text-grey portable-text max-w-lg">
          <PortableText value={content.body} />
        </div>
        <div className="flex mt-8 gap-y-6 flex-wrap w-full">
          {content.primaryCTA && (
            <Button
              className="mr-4"
              href={content.primaryCTA.url}
              hasArrow={false}
              asLink
            >
              {content.primaryCTA.text}
            </Button>
          )}
          {content.secondaryCTA && (
            <CTA href={content.secondaryCTA.url} asLink>
              {content.secondaryCTA.text}
            </CTA>
          )}
        </div>
      </div>
      <div className="hidden md:flex md:col-span-5 lg:col-span-6 flex-col items-center justify-center h-full w-full relative">
        <Image
          alt={'Product Image'}
          src={urlForImage(content.image).auto('format').url()}
          fill
          className="object-contain"
        />
      </div>
    </div>
  )
}
